/**
 * Tag taxonomy mirroring the LLM enum in `api/src/lib/extract.ts`.
 *
 * Tags are grouped into named categories so the filter UI can render
 * one fieldset per group (Type, Music genre, Audience, Setting, Time
 * of day, Price, Format) instead of one giant flat list.
 *
 * Also exposes helpers to dedupe between the primary badges
 * (`eventType`, `priceInfo`, `ageRestriction`, `recurringPattern`) and
 * the generic tag chips so the same value isn't shown twice.
 */

export interface TagGroup {
  /** Stable id (used as React key + URL fragment if ever needed). */
  id: string;
  /** Human-readable label rendered above the chip set. */
  label: string;
  /** Tags belonging to this group, in display order. */
  tags: string[];
}

export const TAG_GROUPS: TagGroup[] = [
  {
    id: 'type',
    label: 'Event type',
    tags: [
      'concert', 'live-music', 'dj-set', 'open-mic', 'karaoke', 'trivia', 'bingo', 'game-night',
      'comedy', 'stand-up', 'improv', 'drag-show', 'burlesque', 'dance-performance', 'theater',
      'film-screening', 'art-show', 'gallery-opening', 'lecture', 'workshop', 'class', 'yoga',
      'fitness', 'run', 'cycling', 'outdoor-activity', 'market', 'craft-fair', 'farmers-market',
      'food-festival', 'beer-garden', 'wine-tasting', 'cocktail-class', 'cooking-class', 'pop-up',
      'fundraiser', 'charity', 'networking', 'community-meeting', 'sports', 'watch-party', 'parade',
      'festival', 'holiday-event', 'birthday-celebration', 'brunch-event', 'happy-hour', 'after-party',
      'open-house', 'book-club', 'art-walk', 'block-party', 'food-truck', 'dance-night'
    ]
  },
  {
    id: 'crafts',
    label: 'Arts & crafts',
    tags: [
      'art-exhibition', 'open-studios', 'pottery', 'ceramics', 'yarn-and-fiber',
      'knitting', 'sewing', 'quilting', 'printmaking', 'jewelry-making',
      'woodworking', 'watercolor', 'drawing', 'sculpture', 'collage', 'mural',
      'photography', 'bookbinding', 'leatherwork', 'glassblowing'
    ]
  },
  {
    id: 'markets',
    label: 'Markets, vintage & antiques',
    tags: [
      'maker-market', 'artisan-market', 'handmade', 'craft-fair', 'flea-market',
      'antique-show', 'vintage-market', 'estate-sale', 'collectibles',
      'pop-up-market', 'farmers-market', 'market'
    ]
  },
  {
    id: 'nature',
    label: 'Nature & plants',
    tags: [
      'native-plants', 'plant-sale', 'garden-tour', 'tree-planting',
      'garden-workshop', 'seed-swap', 'composting', 'pollinator', 'horticulture',
      'nature-walk', 'birding', 'hiking', 'kayak', 'wildlife', 'conservation'
    ]
  },
  {
    id: 'volunteer',
    label: 'Volunteering',
    tags: ['volunteer', 'workday', 'cleanup', 'donation-drive', 'mutual-aid', 'nonprofit']
  },
  {
    id: 'puzzles',
    label: 'Puzzles & games',
    tags: [
      'puzzle', 'puzzle-hunt', 'puzzle-swap', 'escape-room', 'scavenger-hunt',
      'crossword', 'jigsaw-puzzle', 'riddle-night', 'board-games', 'tabletop-games'
    ]
  },
  {
    id: 'performing-arts',
    label: 'Performing arts',
    tags: [
      'theater-production', 'musical-theater', 'opera', 'orchestra', 'symphony',
      'chamber-music', 'ballet', 'dance-class', 'jazz-night', 'auditions'
    ]
  },
  {
    id: 'literary',
    label: 'Literary & spoken word',
    tags: ['poetry', 'spoken-word', 'story-slam', 'author-talk', 'book-signing', 'zine', 'writing-workshop', 'literary-festival']
  },
  {
    id: 'film',
    label: 'Film & cinema',
    tags: ['film-festival', 'indie-film', 'film-society', 'short-films', 'documentary']
  },
  {
    id: 'social-dance',
    label: 'Social dance',
    tags: ['social-dance', 'salsa', 'swing-dance', 'tango', 'zouk', 'line-dancing']
  },
  {
    id: 'repair',
    label: 'Repair & making',
    tags: ['repair-cafe', 'fix-it-clinic', 'tool-library', 'makerspace']
  },
  {
    id: 'heritage',
    label: 'Heritage & history',
    tags: ['history-talk', 'historic-tour', 'heritage-festival', 'architecture-tour']
  },
  {
    id: 'games',
    label: 'Games & tabletop',
    tags: ['tcg-tournament', 'chess', 'esports', 'tabletop-rpg', 'game-store-event']
  },
  {
    id: 'genre',
    label: 'Music genre',
    tags: [
      'rock', 'indie-rock', 'alternative', 'punk', 'metal', 'jazz', 'blues', 'soul', 'r-and-b',
      'hip-hop', 'rap', 'electronic', 'edm', 'house', 'techno', 'folk', 'country', 'bluegrass',
      'reggae', 'latin', 'classical', 'singer-songwriter', 'cover-band', 'tribute-band', 'acoustic',
      'funk', 'gospel', 'world-music'
    ]
  },
  {
    id: 'audience',
    label: 'Audience',
    tags: ['all-ages', 'family-friendly', 'adults-only', '21-plus', '18-plus', 'lgbtq-plus', 'pet-friendly', 'dog-friendly']
  },
  {
    id: 'setting',
    label: 'Setting',
    tags: ['outdoor', 'indoor', 'rooftop', 'patio', 'pop-up-venue']
  },
  {
    id: 'time',
    label: 'Time of day',
    tags: ['brunch', 'afternoon', 'evening', 'late-night']
  },
  {
    id: 'price',
    label: 'Price',
    tags: ['free', 'ticketed', 'cover-charge']
  },
  {
    id: 'format',
    label: 'Format',
    tags: ['recurring', 'one-time', 'weekly', 'monthly']
  }
];

/** Lookup: tag → groupId. Tags not in the taxonomy fall through to "other". */
const TAG_TO_GROUP: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const g of TAG_GROUPS) for (const t of g.tags) m.set(t, g.id);
  return m;
})();

/** Group id for a taxonomy tag, or undefined for tags outside the taxonomy. */
export function tagGroupId(tag: string): string | undefined {
  return TAG_TO_GROUP.get(tag);
}

const OTHER_GROUP: TagGroup = { id: 'other', label: 'Other', tags: [] };

/**
 * Bucket a flat list of tags by group, preserving each group's defined
 * display order. Returns only groups that contain at least one tag.
 * Unknown tags get bucketed into a synthetic "Other" group, sorted A→Z.
 */
export function groupTags(tags: readonly string[]): TagGroup[] {
  const seen = new Set<string>();
  const present = new Set<string>();
  for (const t of tags) {
    if (!t || seen.has(t)) continue;
    seen.add(t);
    present.add(t);
  }
  const out: TagGroup[] = [];
  for (const g of TAG_GROUPS) {
    const inGroup = g.tags.filter((t) => present.has(t));
    if (inGroup.length) out.push({ ...g, tags: sortByLabel(inGroup) });
  }
  const other = [...present]
    .filter((t) => !TAG_TO_GROUP.has(t));
  if (other.length) out.push({ ...OTHER_GROUP, tags: sortByLabel(other) });
  return out;
}

/**
 * Build the set of tag values that are already represented by a primary
 * badge (event type, price, age restriction, recurring pattern), so the
 * generic chip row can suppress them and avoid visual duplicates.
 *
 * Comparison is case-insensitive and tolerant of common synonyms
 * ("21+" / "21-plus", "Free" / "free").
 */
export function badgeCoveredTags(event: {
  eventType?: string | null;
  priceInfo?: string | null;
  isFreeEntry?: boolean | null;
  ageRestriction?: string | null;
  recurringPattern?: string | null;
}): Set<string> {
  const covered = new Set<string>();
  const add = (v: string | null | undefined) => {
    if (!v) return;
    covered.add(v.trim().toLowerCase());
  };
  add(event.eventType);
  add(event.recurringPattern);

  // Price: any non-empty priceInfo or explicit free flag covers "free".
  if (event.isFreeEntry === true || (event.priceInfo && /\bfree\b/i.test(event.priceInfo))) {
    covered.add('free');
  }
  if (event.priceInfo && /ticket/i.test(event.priceInfo)) covered.add('ticketed');
  if (event.priceInfo && /cover/i.test(event.priceInfo)) covered.add('cover-charge');

  // Age: normalize "21+" → "21-plus", "18+" → "18-plus".
  if (event.ageRestriction) {
    const a = event.ageRestriction.trim().toLowerCase();
    covered.add(a);
    if (a === '21+') covered.add('21-plus');
    if (a === '18+') covered.add('18-plus');
    if (a === 'all-ages') covered.add('all-ages');
  }
  return covered;
}

/**
 * Curated display overrides for tags whose default
 * "split-on-hyphen, capitalize first word" rendering would look wrong
 * (acronyms, ampersands, plus signs, glued compound words).
 */
const TAG_LABEL_OVERRIDES: Record<string, string> = {
  // Event type
  'dj-set': 'DJ set',
  'open-mic': 'Open mic',
  'stand-up': 'Stand-up',
  'r-and-b': 'R&B',
  'pop-up': 'Pop-up',
  'pop-up-venue': 'Pop-up venue',
  'after-party': 'After-party',
  'happy-hour': 'Happy hour',
  'food-truck': 'Food truck',
  'food-festival': 'Food festival',
  'farmers-market': 'Farmers market',
  'craft-fair': 'Craft fair',
  'beer-garden': 'Beer garden',
  'wine-tasting': 'Wine tasting',
  'cocktail-class': 'Cocktail class',
  'cooking-class': 'Cooking class',
  'film-screening': 'Film screening',
  'art-show': 'Art show',
  'art-walk': 'Art walk',
  'gallery-opening': 'Gallery opening',
  'block-party': 'Block party',
  'book-club': 'Book club',
  'open-house': 'Open house',
  'watch-party': 'Watch party',
  'game-night': 'Game night',
  'dance-night': 'Dance night',
  'dance-performance': 'Dance performance',
  // Arts & crafts
  'art-exhibition': 'Exhibition / on view',
  'open-studios': 'Open studios',
  'yarn-and-fiber': 'Yarn & fibre',
  'jewelry-making': 'Jewelry making',
  'maker-market': "Makers' market",
  'artisan-market': 'Artisan market',
  'flea-market': 'Flea market',
  'antique-show': 'Antique show',
  'vintage-market': 'Vintage market',
  'estate-sale': 'Estate sale',
  'pop-up-market': 'Pop-up market',
  // Nature & plants
  'native-plants': 'Native plants',
  'plant-sale': 'Plant sale',
  'garden-tour': 'Garden tour',
  'tree-planting': 'Tree planting',
  'garden-workshop': 'Garden workshop',
  'seed-swap': 'Seed swap',
  'nature-walk': 'Nature walk',
  // Volunteering
  'donation-drive': 'Donation drive',
  'mutual-aid': 'Mutual aid',
  // Puzzles & games
  'puzzle-hunt': 'Puzzle hunt',
  'puzzle-swap': 'Puzzle swap',
  'escape-room': 'Escape room',
  'scavenger-hunt': 'Scavenger hunt',
  'jigsaw-puzzle': 'Jigsaw puzzle',
  'riddle-night': 'Riddle night',
  'board-games': 'Board games',
  'tabletop-games': 'Tabletop games',
  // Literary, film, dance, repair, heritage & games
  'spoken-word': 'Spoken word',
  'story-slam': 'Story slam',
  'author-talk': 'Author talk',
  'book-signing': 'Book signing',
  'writing-workshop': 'Writing workshop',
  'literary-festival': 'Literary festival',
  'film-festival': 'Film festival',
  'indie-film': 'Indie film',
  'film-society': 'Film society',
  'short-films': 'Short films',
  'social-dance': 'Social dance',
  'swing-dance': 'Swing dance',
  'line-dancing': 'Line dancing',
  'repair-cafe': 'Repair cafe',
  'fix-it-clinic': 'Fix-it clinic',
  'tool-library': 'Tool library',
  'tcg-tournament': 'TCG tournament',
  'tabletop-rpg': 'Tabletop RPG',
  'game-store-event': 'Game store event',
  'history-talk': 'History talk',
  'historic-tour': 'Historic tour',
  'heritage-festival': 'Heritage festival',
  'architecture-tour': 'Architecture tour',  // Performing arts
  'theater-production': 'Theater production',
  'musical-theater': 'Musical theater',
  'chamber-music': 'Chamber music',
  'dance-class': 'Dance class',
  'jazz-night': 'Jazz night',
  'drag-show': 'Drag show',
  'birthday-celebration': 'Birthday celebration',
  'brunch-event': 'Brunch event',
  'community-meeting': 'Community meeting',
  'holiday-event': 'Holiday event',
  'outdoor-activity': 'Outdoor activity',
  'live-music': 'Live music',
  // Music genre
  'indie-rock': 'Indie rock',
  'hip-hop': 'Hip-hop',
  'edm': 'EDM',
  'singer-songwriter': 'Singer-songwriter',
  'cover-band': 'Cover band',
  'tribute-band': 'Tribute band',
  'world-music': 'World music',
  // Audience
  'all-ages': 'All ages',
  'family-friendly': 'Family-friendly',
  'adults-only': 'Adults only',
  '21-plus': '21+',
  '18-plus': '18+',
  'lgbtq-plus': 'LGBTQ+',
  'pet-friendly': 'Pet-friendly',
  'dog-friendly': 'Dog-friendly',
  // Setting
  'late-night': 'Late night',
  // Price
  'cover-charge': 'Cover charge',
  // Format
  'one-time': 'One-time'
};

/**
 * Curated emoji per tag/category slug.
 *
 * Used as a quiet leading glyph in chip rows and dropdown options to
 * make scanning faster. Keep emojis culturally neutral and visually
 * distinct from each other within the same group.
 *
 * Unknown slugs render with no icon — that's the deliberate fallback.
 */
const TAG_ICONS: Record<string, string> = {
  // Google Place categories
  'art_gallery': '🎨',
  'bar': '🍸',
  'book_store': '📚',
  'cafe': '☕',
  'manual': '✍️',
  'night_club': '🪩',
  'park': '🌳',
  'performing_arts_theater': '🎭',
  'pub': '🍺',
  'restaurant': '🍽️',
  'wine_bar': '🍷',
  // Event type
  'concert': '🎤',
  'live-music': '🎵',
  'dj-set': '🎧',
  'open-mic': '🎙️',
  'karaoke': '🎤',
  'trivia': '🧠',
  'bingo': '🎱',
  'game-night': '🎲',
  'comedy': '😂',
  'stand-up': '🎙️',
  'improv': '🎭',
  'drag-show': '👑',
  'burlesque': '💃',
  'dance-performance': '💃',
  'theater': '🎭',
  'film-screening': '🎬',
  'art-show': '🖼️',
  'art-walk': '🚶',
  'gallery-opening': '🖼️',
  'lecture': '🗣️',
  'workshop': '🛠️',
  'class': '📚',
  'yoga': '🧘',
  'fitness': '💪',
  'run': '🏃',
  'cycling': '🚴',
  'outdoor-activity': '🌲',
  'market': '🛍️',
  'craft-fair': '🧵',
  'farmers-market': '🥕',
  // Arts & crafts
  'art-exhibition': '🖼️',
  'open-studios': '🚪',
  'pottery': '🏺',
  'ceramics': '🏺',
  'yarn-and-fiber': '🧶',
  'knitting': '🧶',
  'sewing': '🧵',
  'quilting': '🧵',
  'printmaking': '🖨️',
  'jewelry-making': '💎',
  'woodworking': '🪵',
  'watercolor': '🎨',
  'drawing': '✏️',
  'sculpture': '🗿',
  'collage': '📰',
  'mural': '🎨',
  'photography': '📷',
  'bookbinding': '📕',
  'leatherwork': '🧳',
  'glassblowing': '🔥',
  // Markets, vintage & antiques
  'maker-market': '🛍️',
  'artisan-market': '🛍️',
  'handmade': '✋',
  'flea-market': '🧳',
  'antique-show': '🏺',
  'vintage-market': '👗',
  'estate-sale': '🏠',
  'collectibles': '🃏',
  'pop-up-market': '📍',
  // Nature & plants
  'native-plants': '🌾',
  'plant-sale': '🪴',
  'garden-tour': '🌻',
  'tree-planting': '🌳',
  'garden-workshop': '🧑‍🌾',
  'seed-swap': '🌱',
  'composting': '♻️',
  'pollinator': '🐝',
  'horticulture': '🌷',
  'nature-walk': '🥾',
  'birding': '🐦',
  'hiking': '🥾',
  'kayak': '🛶',
  'wildlife': '🦉',
  'conservation': '🌿',
  // Volunteering
  'volunteer': '🙌',
  'workday': '🛠️',
  'cleanup': '🧹',
  'donation-drive': '🎁',
  'mutual-aid': '🤝',
  'nonprofit': '💚',
  // Puzzles & games
  'puzzle': '🧩',
  'puzzle-hunt': '🧩',
  'puzzle-swap': '🧩',
  'escape-room': '🔐',
  'scavenger-hunt': '🗺️',
  'crossword': '✏️',
  'jigsaw-puzzle': '🧩',
  'riddle-night': '❓',
  'board-games': '🎲',
  'tabletop-games': '🎲',
  // Literary, film, dance, repair, heritage & games
  'poetry': '📜',
  'spoken-word': '🎤',
  'story-slam': '📖',
  'author-talk': '📚',
  'book-signing': '✍️',
  'zine': '📰',
  'writing-workshop': '✍️',
  'literary-festival': '📚',
  'film-festival': '🎬',
  'indie-film': '🎞️',
  'film-society': '🎞️',
  'short-films': '🎞️',
  'documentary': '🎥',
  'social-dance': '💃',
  'salsa': '💃',
  'swing-dance': '🕺',
  'tango': '💃',
  'zouk': '💃',
  'line-dancing': '🤠',
  'repair-cafe': '🔧',
  'fix-it-clinic': '🔧',
  'tool-library': '🧰',
  'makerspace': '🛠️',
  'tcg-tournament': '🃏',
  'chess': '♟️',
  'esports': '🎮',
  'tabletop-rpg': '🎲',
  'game-store-event': '🎮',
  'history-talk': '🏛️',
  'historic-tour': '🏛️',
  'heritage-festival': '🏛️',
  'architecture-tour': '🏛️',  // Performing arts
  'theater-production': '🎭',
  'musical-theater': '🎭',
  'opera': '🎼',
  'orchestra': '🎻',
  'symphony': '🎻',
  'chamber-music': '🎻',
  'ballet': '🩰',
  'dance-class': '💃',
  'jazz-night': '🎷',
  'auditions': '📋',
  'food-festival': '🍴',
  'beer-garden': '🍻',
  'wine-tasting': '🍷',
  'cocktail-class': '🍸',
  'cooking-class': '👨‍🍳',
  'pop-up': '📍',
  'fundraiser': '💝',
  'charity': '💝',
  'networking': '🤝',
  'community-meeting': '🗳️',
  'sports': '⚽',
  'watch-party': '📺',
  'parade': '🎉',
  'festival': '🎊',
  'holiday-event': '🎄',
  'birthday-celebration': '🎂',
  'brunch-event': '🥐',
  'happy-hour': '🍹',
  'after-party': '🪩',
  'open-house': '🏡',
  'book-club': '📖',
  'block-party': '🏘️',
  'food-truck': '🚚',
  'dance-night': '🕺',
  // Music genre
  'rock': '🎸',
  'indie-rock': '🎸',
  'alternative': '🎸',
  'punk': '🤘',
  'metal': '🤘',
  'jazz': '🎷',
  'blues': '🎺',
  'soul': '🎤',
  'r-and-b': '🎤',
  'hip-hop': '🎤',
  'rap': '🎤',
  'electronic': '🎛️',
  'edm': '🎛️',
  'house': '🎛️',
  'techno': '🎛️',
  'folk': '🪕',
  'country': '🤠',
  'bluegrass': '🪕',
  'reggae': '🟢',
  'latin': '💃',
  'classical': '🎻',
  'singer-songwriter': '🎼',
  'cover-band': '🎸',
  'tribute-band': '🎸',
  'acoustic': '🎼',
  'funk': '🎶',
  'gospel': '🙏',
  'world-music': '🌍',
  // Audience
  'all-ages': '👨‍👩‍👧',
  'family-friendly': '👨‍👩‍👧',
  'adults-only': '🔞',
  '21-plus': '🍷',
  '18-plus': '🔞',
  'lgbtq-plus': '🏳️‍🌈',
  'pet-friendly': '🐾',
  'dog-friendly': '🐶',
  // Setting
  'outdoor': '☀️',
  'indoor': '🏠',
  'rooftop': '🏙️',
  'patio': '🌿',
  'pop-up-venue': '📍',
  // Time of day
  'brunch': '🥐',
  'afternoon': '🌤️',
  'evening': '🌆',
  'late-night': '🌙',
  // Price
  'free': '🆓',
  'ticketed': '🎟️',
  'cover-charge': '💵',
  // Format
  'recurring': '🔁',
  'weekly': '📅',
  'monthly': '🗓️'
};

/**
 * Returns the curated emoji for a slug, or an empty string when no
 * icon is defined. Comparison is case-insensitive on the trimmed slug.
 */
export function tagIcon(tag: string | null | undefined): string {
  if (!tag) return '';
  return TAG_ICONS[tag.trim().toLowerCase()] ?? '';
}

/**
 * Sort a list of slugs alphabetically by their *display* label, so that
 * UI ordering matches what the user actually reads (e.g. `night_club`
 * shows under "N", not "n_").
 */
export function sortByLabel(values: readonly string[]): string[] {
  return [...values].sort((a, b) =>
    formatTag(a).localeCompare(formatTag(b), undefined, { sensitivity: 'base' })
  );
}

/**
 * Render a slug-style tag (e.g. "live-music", "21-plus") as a
 * user-friendly label (e.g. "Live music", "21+"). Honors the curated
 * overrides above; otherwise replaces hyphens/underscores with spaces
 * and capitalizes the first letter (sentence case).
 *
 * Pure presentation only — the underlying tag value is unchanged so
 * filter state, URL params, and API payloads continue to use the slug.
 */
export function formatTag(tag: string | null | undefined): string {
  if (!tag) return '';
  const trimmed = tag.trim();
  if (!trimmed) return '';
  const key = trimmed.toLowerCase();
  if (TAG_LABEL_OVERRIDES[key]) return TAG_LABEL_OVERRIDES[key];
  const spaced = trimmed.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Filter a tag list down to the chips worth rendering alongside the
 * primary badges: dedupes case-insensitively and drops any tag already
 * covered by a badge.
 */
export function visibleChipTags(
  tags: readonly string[] | undefined,
  event: Parameters<typeof badgeCoveredTags>[0]
): string[] {
  if (!tags?.length) return [];
  const covered = badgeCoveredTags(event);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tags) {
    if (!t) continue;
    const k = t.trim().toLowerCase();
    if (!k || seen.has(k) || covered.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}
